import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { setUserMenuPreference } from "redux/action-creators/user";
import { getUserPrefersMedical } from "redux/selectors/user";
import { getLongTitle, getShortTitle, MenuType } from "utils/menuTypeUtils";

import Button from "components/botanic/Button";
import { InformationalModal } from "components/botanic/Modal/InformationalModal";
import Radio from "components/botanic/Radio";
import SolidArrowIcon from "components/Icons/arrows/SolidArrowIcon";
import RadioGroup from "components/RadioGroup";

const MODAL_OPTIONS = Object.freeze([
  { label: getLongTitle(MenuType.Rec), value: MenuType.Rec },
  { label: `${getLongTitle(MenuType.Med)}`, value: MenuType.Med },
]);

const MenuTypeButton = ({
  onMenuTypeChange,
  theme = "light",
}: {
  onMenuTypeChange?: (value: MenuType.Med | MenuType.Rec) => void;
  theme?: "light" | "dark";
}) => {
  const dispatch = useDispatch();

  const userPrefersMedical = useSelector(getUserPrefersMedical);
  const initialMenuType = userPrefersMedical ? MenuType.Med : MenuType.Rec;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [selectedMenuType, setSelectedMenuType] = useState<
    MenuType.Med | MenuType.Rec
  >();

  useEffect(() => {
    if (userPrefersMedical !== undefined && !selectedMenuType) {
      setSelectedMenuType(initialMenuType);
    }
  }, [userPrefersMedical]);

  const shortTitle = getShortTitle(initialMenuType);

  const onDismissed = () => {
    if (selectedMenuType && initialMenuType !== selectedMenuType) {
      dispatch(setUserMenuPreference(selectedMenuType));
      onMenuTypeChange?.(selectedMenuType);
    }

    setOpen(false);
  };

  return (
    <>
      <button
        className={classnames("px-md py-sm rounded flex font-bold", {
          "bg-green text-white": theme === "dark",
          "bg-leafly-white text-black": theme === "light",
        })}
        ref={buttonRef}
        onClick={() => setOpen(true)}
        data-testid="menu-type-button"
        aria-label={`Menu: ${shortTitle}`}
      >
        <span>Menu:</span>
        <span
          className={classnames("ml-md flex", {
            "text-green": theme === "light",
            "text-white": theme === "dark",
          })}
        >
          <span>{shortTitle}</span>
          <SolidArrowIcon />
        </span>
      </button>

      {open && (
        <InformationalModal
          onDismissed={onDismissed}
          returnFocusRef={buttonRef}
          title="Choose your menu"
        >
          <RadioGroup name="menuType">
            {MODAL_OPTIONS.map(({ value, label }, i) => (
              <Radio
                key={`menu-type-radio-${i}`}
                id={`menu-type-radio-${value}`}
                checked={selectedMenuType === value}
                ariaLabel={label}
                className="my-sm"
                onChange={() => setSelectedMenuType(value)}
                data-testid={`menu-type-button-option-${value}`}
              >
                <>
                  <span>{label}</span>
                  {value === MenuType.Med &&
                    selectedMenuType === MenuType.Med && (
                      <p className="text-xs text-error">
                        You need a valid medical ID to shop from a medical menu.
                        Your ID number is required at checkout. You can choose
                        to save your med ID to your Leafly profile for faster
                        checkout.
                      </p>
                    )}
                </>
              </Radio>
            ))}
          </RadioGroup>
          <Button
            size="small"
            onClick={onDismissed}
            className="mt-3"
            data-testid="menu-type-button-confirm"
          >
            Confirm
          </Button>
        </InformationalModal>
      )}
    </>
  );
};

export default MenuTypeButton;
